import { useState, useEffect, useRef } from "react";
import { emailOtp, validateResetEmail } from "@/lib/auth-client";
import { useAuthStore } from "@/store/useAuthStore";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";

type Step = "code" | "new-password" | "google-account";
type CodeError = "invalid" | "expired" | null;

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordModalProps) => {
  const [step, setStep] = useState<Step>("code");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<CodeError>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifiedOtp, setVerifiedOtp] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const { showNotification } = useNotificationStore();
  const { user } = useAuthStore();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const email = user?.email || "";

  // Send OTP when modal opens
  useEffect(() => {
    if (isOpen && email && !codeSent) {
      setIsSendingCode(true);

      // First validate the email can receive a password reset
      validateResetEmail(email)
        .then((validation) => {
          if (!validation.valid) {
            if (validation.error === "GOOGLE_ACCOUNT") {
              setStep("google-account");
              setIsSendingCode(false);
            } else {
              showNotification(
                "Unable to change password for this account.",
                NotificationType.WARNING,
              );
              onClose();
            }
            return;
          }

          // Email is valid - send the OTP
          return emailOtp.sendVerificationOtp({
            email,
            type: "forget-password",
          });
        })
        .then((result) => {
          if (!result) return; // Validation failed, already handled
          if (result.error) {
            showNotification(
              result.error.message || "Failed to send verification code.",
              NotificationType.WARNING,
            );
          } else {
            showNotification(
              "Verification code sent to your email.",
              NotificationType.SUCCESS,
            );
            setCodeSent(true);
          }
        })
        .catch(() => {
          showNotification(
            "Failed to send verification code. Please try again.",
            NotificationType.WARNING,
          );
        })
        .finally(() => {
          setIsSendingCode(false);
        });
    }
  }, [isOpen, email, codeSent, showNotification, onClose]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep("code");
      setCode(["", "", "", "", "", ""]);
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordError(null);
      setCodeError(null);
      setVerifiedOtp("");
      setResendCooldown(0);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setCodeSent(false);
    }
  }, [isOpen]);

  // Focus first OTP box when entering code step
  useEffect(() => {
    if (step === "code" && !isSendingCode) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [step, isSendingCode]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    if (codeError) setCodeError(null);

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      e.preventDefault();
      const newCode = [...code];
      newCode[index - 1] = "";
      setCode(newCode);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\s/g, "");
    const digitsOnly = pastedData.replace(/\D/g, "").slice(0, 6);
    if (!digitsOnly) return;
    if (codeError) setCodeError(null);

    const newCode = [...code];
    for (let i = 0; i < digitsOnly.length; i++) {
      newCode[i] = digitsOnly[i];
    }
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex((d) => !d);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = code.join("");
    if (otpCode.length !== 6) return;

    setIsLoading(true);
    setCodeError(null);

    try {
      const { error } = await emailOtp.checkVerificationOtp({
        email,
        type: "forget-password",
        otp: otpCode,
      });

      if (error) {
        if (
          error.code === "INVALID_OTP" ||
          error.message?.includes("invalid")
        ) {
          setCodeError("invalid");
        } else if (
          error.code === "OTP_EXPIRED" ||
          error.message?.includes("expired")
        ) {
          setCodeError("expired");
        } else {
          showNotification(
            error.message || "Failed to verify code.",
            NotificationType.WARNING,
          );
        }
        return;
      }

      setVerifiedOtp(otpCode);
      setStep("new-password");
    } catch (err) {
      showNotification("Failed to verify code.", NotificationType.WARNING);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setCodeError(null);
    try {
      // Validate email is still eligible (safety check)
      const validation = await validateResetEmail(email);
      if (!validation.valid) {
        if (validation.error === "GOOGLE_ACCOUNT") {
          setStep("google-account");
        } else {
          showNotification(
            "Unable to change password for this account.",
            NotificationType.WARNING,
          );
        }
        return;
      }

      const { error } = await emailOtp.sendVerificationOtp({
        email,
        type: "forget-password",
      });

      if (error) {
        showNotification(
          error.message || "Failed to resend code.",
          NotificationType.WARNING,
        );
      } else {
        showNotification("Verification code resent.", NotificationType.SUCCESS);
        setResendCooldown(15);
      }
    } catch (err) {
      showNotification("Failed to resend code.", NotificationType.WARNING);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords don't match.");
      return;
    }

    setIsLoading(true);
    setPasswordError(null);

    try {
      const { error } = await emailOtp.resetPassword({
        email,
        otp: verifiedOtp,
        password: newPassword,
      });

      if (error) {
        if (
          error.code === "INVALID_OTP" ||
          error.message?.includes("invalid")
        ) {
          setCodeError("invalid");
          setStep("code");
          setCode(["", "", "", "", "", ""]);
        } else if (
          error.code === "OTP_EXPIRED" ||
          error.message?.includes("expired")
        ) {
          setCodeError("expired");
          setStep("code");
          setCode(["", "", "", "", "", ""]);
        } else {
          showNotification(
            error.message || "Failed to update password.",
            NotificationType.WARNING,
          );
        }
        return;
      }

      showNotification(
        "Password updated successfully!",
        NotificationType.SUCCESS,
      );
      onClose();
    } catch (err) {
      showNotification("Failed to update password.", NotificationType.WARNING);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep("code");
    setCode(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordError(null);
    setCodeError(null);
    setVerifiedOtp("");
    setCodeSent(false);
    onClose();
  };

  const isCodeComplete = code.every((digit) => digit !== "");

  if (!isOpen) return null;

  return (
    <div>
      <div className="et-settings-form">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-nunito font-bold text-[20px] text-gray-900">
            Setup a new password
          </h2>
        </div>

        {step === "code" ? (
          isSendingCode ? (
            <div className="flex flex-col items-center gap-6">
              <p className="font-mulish font-medium text-[15px] leading-snug text-gray-600 text-center">
                Sending verification code to
                <br />
                <span className="font-bold italic">{email}</span>
              </p>
              <div className="flex items-center justify-center h-[57px] px-6 border border-[#F5F5F5] rounded-[10px] gap-[10px]">
                <svg
                  className="animate-spin"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M7 14C3.143 14 0 10.857 0 7C0 5.691 0.364 4.417 1.05 3.311C1.253 2.982 1.687 2.884 2.016 3.087C2.345 3.29 2.443 3.72399 2.24 4.05299C1.694 4.93499 1.4 5.957 1.4 7C1.4 10.087 3.913 12.6 7 12.6C10.087 12.6 12.6 10.087 12.6 7C12.6 3.913 10.087 1.4 7 1.4C6.615 1.4 6.3 1.085 6.3 0.7C6.3 0.315 6.615 0 7 0C10.857 0 14 3.143 14 7C14 10.857 10.857 14 7 14Z"
                    fill="black"
                  />
                </svg>
                <span className="font-mulish font-bold text-[15px] leading-none text-gray-900">
                  Sending code...
                </span>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="font-nunito font-semibold text-[16px] text-[#28543D] hover:text-[#28543D]/80 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="flex flex-col gap-6">
              <p className="font-mulish font-medium text-[15px] leading-snug text-gray-600 text-center">
                Enter the 6-digit code sent to{" "}
                <span className="font-bold italic">{email}</span>.<br />
                If you can't find it, please reach out to{" "}
                <span className="font-bold">support@evertutor.ai</span>
              </p>

              <div>
                <div
                  className="flex justify-center gap-3"
                  onPaste={handlePaste}
                >
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      disabled={isLoading}
                      className={`w-12 h-14 text-center text-2xl font-semibold bg-white border rounded-[6px] focus:outline-none focus:ring-2 focus:ring-[#28543D] focus:border-transparent transition-all duration-200 text-[#294B39] disabled:bg-[#F3EDE2] ${
                        codeError ? "border-red-300" : "border-[#D4D3C5]"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex justify-between items-center mt-3">
                  <div>
                    {codeError === "invalid" && (
                      <p className="text-red-500 text-sm">
                        Wrong code entered.
                      </p>
                    )}
                    {codeError === "expired" && (
                      <p className="text-red-500 text-sm">Code expired.</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isLoading || resendCooldown > 0}
                    className={`font-mulish font-bold text-[14px] leading-none px-3 py-2 rounded transition-colors ${
                      resendCooldown > 0
                        ? "text-[#28543D]/40 cursor-not-allowed"
                        : "text-[#28543D] hover:text-[#28543D]/80"
                    }`}
                  >
                    {resendCooldown > 0
                      ? `Resend (${resendCooldown}s)`
                      : "Resend code"}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4">
                <button
                  type="submit"
                  disabled={!isCodeComplete || isLoading}
                  className="et-primary"
                >
                  Verify
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="et-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          )
        ) : step === "google-account" ? (
          <div className="flex flex-col gap-4">
            <p className="font-mulish font-bold text-[15px] leading-snug text-gray-700 text-center">
              You don't have a password for your EverTutor account.
              <br />
              Please use 'Sign in with Google' to access your account.
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleClose}
                className="font-nunito font-semibold text-[16px] text-[#28543D] hover:text-[#28543D]/80 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdatePassword} className="flex flex-col gap-4">
            <div>
              <label className="block font-nunito font-bold text-[15px] leading-none text-gray-400 mb-2">
                Enter New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                  className={`w-full h-[60px] py-[10px] px-[20px] pr-12 bg-white border rounded-[10px] font-mulish font-bold text-[20px] leading-none text-gray-900 focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D] ${
                    passwordError && passwordError.includes("6 characters")
                      ? "border-red-300"
                      : "border-[#E0E0E0]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#28543D] hover:text-[#28543D]/80 transition-colors"
                >
                  {showNewPassword ? (
                    <svg
                      width="20"
                      height="10"
                      viewBox="0 0 16 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.4621 1.81541C13.6862 1.6855 13.9732 1.76181 14.1032 1.98597L15.8935 5.07412C16.0234 5.29822 15.9471 5.58524 15.723 5.7152C15.4989 5.84511 15.2119 5.76881 15.0819 5.54464L13.2915 2.45649C13.1616 2.2324 13.2379 1.94538 13.4621 1.81541Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.94917 3.47813C10.2044 3.43322 10.4476 3.60359 10.4926 3.85871L11.0476 7.00941C11.0925 7.26459 10.9222 7.50783 10.6671 7.5528C10.4119 7.59771 10.1686 7.42733 10.1237 7.17221L9.56858 4.02152C9.52368 3.7664 9.69405 3.5231 9.94917 3.47813Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.00755 3.47139C6.26267 3.51624 6.43316 3.75941 6.38832 4.01459L5.83323 7.17311C5.78839 7.42823 5.54521 7.59872 5.29003 7.55388C5.03491 7.50904 4.86441 7.26586 4.90926 7.01068L5.46434 3.85216C5.50919 3.59704 5.75236 3.42655 6.00755 3.47139Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.49389 1.81492C2.7183 1.94439 2.79529 2.23122 2.66584 2.45563L0.875497 5.55942C0.746048 5.78383 0.459192 5.86082 0.234782 5.73135C0.0103707 5.60194 -0.0666157 5.31505 0.0628333 5.09064L1.85318 1.98686C1.98262 1.76244 2.26948 1.68545 2.49389 1.81492Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.182108 0.104184C0.383647 -0.0586205 0.678989 -0.0271605 0.841775 0.174359C2.1034 1.73635 4.39019 3.68237 7.98226 3.68237C11.5743 3.68237 13.861 1.73635 15.1227 0.174359C15.2855 -0.0271605 15.5808 -0.0586205 15.7823 0.104184C15.9839 0.266988 16.0153 0.562324 15.8525 0.763844C14.4873 2.45418 11.9581 4.62054 7.98226 4.62054C4.00634 4.62054 1.47719 2.45418 0.111933 0.763844C-0.0508522 0.562324 -0.0194297 0.266988 0.182108 0.104184Z"
                        fill="#28543D"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.2482 10.1701C4.79637 11.5422 3.85959 13.1933 3.45058 14.267C3.30312 14.6541 2.8698 14.8483 2.48272 14.7009C2.09564 14.5534 1.90139 14.1201 2.04885 13.733C2.52873 12.4733 3.58362 10.6245 5.21789 9.07992C6.86097 7.52706 9.12689 6.25 11.9997 6.25C14.8725 6.25 17.1385 7.52706 18.7815 9.07992C20.4158 10.6245 21.4707 12.4733 21.9506 13.733C22.098 14.1201 21.9038 14.5534 21.5167 14.7009C21.1296 14.8483 20.6963 14.6541 20.5488 14.267C20.1398 13.1933 19.2031 11.5422 17.7512 10.1701C16.3082 8.80628 14.3936 7.75 11.9997 7.75C9.60588 7.75 7.69124 8.80628 6.2482 10.1701ZM11.9997 11.75C10.7571 11.75 9.74971 12.7574 9.74971 14C9.74971 15.2426 10.7571 16.25 11.9997 16.25C13.2424 16.25 14.2497 15.2426 14.2497 14C14.2497 12.7574 13.2424 11.75 11.9997 11.75ZM8.24971 14C8.24971 11.9289 9.92865 10.25 11.9997 10.25C14.0708 10.25 15.7497 11.9289 15.7497 14C15.7497 16.0711 14.0708 17.75 11.9997 17.75C9.92865 17.75 8.24971 16.0711 8.24971 14Z"
                        fill="#28543D"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && passwordError.includes("6 characters") && (
                <p className="text-red-500 text-sm mt-2">{passwordError}</p>
              )}
            </div>

            <div>
              <label className="block font-nunito font-bold text-[15px] leading-none text-gray-400 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmNewPassword}
                  onChange={(e) => {
                    setConfirmNewPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                  className="w-full h-[60px] py-[10px] px-[20px] pr-12 bg-white border border-[#E0E0E0] rounded-[10px] font-mulish font-bold text-[20px] leading-none text-gray-900 focus:outline-none focus:border-[#28543D9E] focus:shadow-[0_0_4px_0_#28543D4D]"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#28543D] hover:text-[#28543D]/80 transition-colors"
                >
                  {showConfirmPassword ? (
                    <svg
                      width="20"
                      height="10"
                      viewBox="0 0 16 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.4621 1.81541C13.6862 1.6855 13.9732 1.76181 14.1032 1.98597L15.8935 5.07412C16.0234 5.29822 15.9471 5.58524 15.723 5.7152C15.4989 5.84511 15.2119 5.76881 15.0819 5.54464L13.2915 2.45649C13.1616 2.2324 13.2379 1.94538 13.4621 1.81541Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.94917 3.47813C10.2044 3.43322 10.4476 3.60359 10.4926 3.85871L11.0476 7.00941C11.0925 7.26459 10.9222 7.50783 10.6671 7.5528C10.4119 7.59771 10.1686 7.42733 10.1237 7.17221L9.56858 4.02152C9.52368 3.7664 9.69405 3.5231 9.94917 3.47813Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.00755 3.47139C6.26267 3.51624 6.43316 3.75941 6.38832 4.01459L5.83323 7.17311C5.78839 7.42823 5.54521 7.59872 5.29003 7.55388C5.03491 7.50904 4.86441 7.26586 4.90926 7.01068L5.46434 3.85216C5.50919 3.59704 5.75236 3.42655 6.00755 3.47139Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.49389 1.81492C2.7183 1.94439 2.79529 2.23122 2.66584 2.45563L0.875497 5.55942C0.746048 5.78383 0.459192 5.86082 0.234782 5.73135C0.0103707 5.60194 -0.0666157 5.31505 0.0628333 5.09064L1.85318 1.98686C1.98262 1.76244 2.26948 1.68545 2.49389 1.81492Z"
                        fill="#28543D"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.182108 0.104184C0.383647 -0.0586205 0.678989 -0.0271605 0.841775 0.174359C2.1034 1.73635 4.39019 3.68237 7.98226 3.68237C11.5743 3.68237 13.861 1.73635 15.1227 0.174359C15.2855 -0.0271605 15.5808 -0.0586205 15.7823 0.104184C15.9839 0.266988 16.0153 0.562324 15.8525 0.763844C14.4873 2.45418 11.9581 4.62054 7.98226 4.62054C4.00634 4.62054 1.47719 2.45418 0.111933 0.763844C-0.0508522 0.562324 -0.0194297 0.266988 0.182108 0.104184Z"
                        fill="#28543D"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.2482 10.1701C4.79637 11.5422 3.85959 13.1933 3.45058 14.267C3.30312 14.6541 2.8698 14.8483 2.48272 14.7009C2.09564 14.5534 1.90139 14.1201 2.04885 13.733C2.52873 12.4733 3.58362 10.6245 5.21789 9.07992C6.86097 7.52706 9.12689 6.25 11.9997 6.25C14.8725 6.25 17.1385 7.52706 18.7815 9.07992C20.4158 10.6245 21.4707 12.4733 21.9506 13.733C22.098 14.1201 21.9038 14.5534 21.5167 14.7009C21.1296 14.8483 20.6963 14.6541 20.5488 14.267C20.1398 13.1933 19.2031 11.5422 17.7512 10.1701C16.3082 8.80628 14.3936 7.75 11.9997 7.75C9.60588 7.75 7.69124 8.80628 6.2482 10.1701ZM11.9997 11.75C10.7571 11.75 9.74971 12.7574 9.74971 14C9.74971 15.2426 10.7571 16.25 11.9997 16.25C13.2424 16.25 14.2497 15.2426 14.2497 14C14.2497 12.7574 13.2424 11.75 11.9997 11.75ZM8.24971 14C8.24971 11.9289 9.92865 10.25 11.9997 10.25C14.0708 10.25 15.7497 11.9289 15.7497 14C15.7497 16.0711 14.0708 17.75 11.9997 17.75C9.92865 17.75 8.24971 16.0711 8.24971 14Z"
                        fill="#28543D"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {passwordError && passwordError.includes("match") && (
              <p className="text-red-500 text-sm text-center">
                {passwordError}
              </p>
            )}

            <div className="flex items-center justify-center gap-4 mt-2">
              {isLoading ? (
                <div className="flex items-center justify-center h-[57px] px-6 border border-[#F5F5F5] rounded-[10px] gap-[10px]">
                  <svg
                    className="animate-spin"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M7 14C3.143 14 0 10.857 0 7C0 5.691 0.364 4.417 1.05 3.311C1.253 2.982 1.687 2.884 2.016 3.087C2.345 3.29 2.443 3.72399 2.24 4.05299C1.694 4.93499 1.4 5.957 1.4 7C1.4 10.087 3.913 12.6 7 12.6C10.087 12.6 12.6 10.087 12.6 7C12.6 3.913 10.087 1.4 7 1.4C6.615 1.4 6.3 1.085 6.3 0.7C6.3 0.315 6.615 0 7 0C10.857 0 14 3.143 14 7C14 10.857 10.857 14 7 14Z"
                      fill="black"
                    />
                  </svg>
                  <span className="font-mulish font-bold text-[15px] leading-none text-gray-900">
                    Updating...
                  </span>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={!newPassword || !confirmNewPassword}
                  className={`h-[57px] py-[15px] px-[20px] font-nunito font-bold text-[20px] leading-none rounded-[10px] transition-colors ${
                    newPassword && confirmNewPassword
                      ? "bg-[#28543D] text-white hover:bg-[#28543D]/90"
                      : "bg-[#28543D]/20 text-white cursor-not-allowed"
                  }`}
                >
                  Update Password
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="font-nunito font-semibold text-[16px] text-[#28543D] hover:text-[#28543D]/80 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
